<template>
  <div class="app-container">
    <div class="topbar">
      <span class="pager">
        <el-button icon="el-icon-arrow-left" :disabled="!hasPrevious" :loading="loading" @click="previousPage" />
        <el-button class="prev" icon="el-icon-arrow-right" :disabled="!hasNext" :loading="loading" @click="nextPage" />
        <span class="page-number">{{ $t('common.page') + ' ' + currentPage }}</span>
      </span>
      <editor-button v-show="filtersActive()" action="clearFilters" :loading="loading" @click="clearAllFilters" />
      <span style="float:right">
        <el-button v-show="!showCount()" :disabled="counting" @click="countItems">#</el-button>
        <span v-show="showCount()" class="counter">{{ $t('common.total') + ' ' + count }}</span>
        <editor-button action="cancel" @click="close" />
      </span>
    </div>
    <div ref="tableContainer" class="crud-table">
      <el-table
        v-loading="loading"
        :data="items"
        border
        fit
        :max-height="getMaxTableHeight()"
        @row-dblclick="rowDblClick"
        @sort-change="sortChange"
      >
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['ticketNumber']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.ticketNumber')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.ticketNumber')" width="150px">
            <template slot-scope="scope">
              <span>{{ scope.row['ticketNumber'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['customerReference']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.customerReference')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.customerReference')" width="150px">
            <template slot-scope="scope">
              <span>{{ scope.row['customerReference'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['consumerName']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.consumerName')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.consumerName')" min-width="160px">
            <template slot-scope="scope">
              <span>{{ scope.row['consumerName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['storeName']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.storeName')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.storeName')" min-width="160px">
            <template slot-scope="scope">
              <span>{{ scope.row['storeName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['fabricName']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.fabricName')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.fabricName')" min-width="140px">
            <template slot-scope="scope">
              <span>{{ scope.row['fabricName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['fabricStatus']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in $store.getters['option/loadedOptions']('fabricStatus')"
                :key="item['code']"
                :label="item['description']"
                :value="item['code']"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.fabricStatus')" min-width="100px">
            <template slot-scope="scope">
              <span>{{ formatFabricStatus(scope.row['fabricStatus']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <el-table-column :label="$t('common.fabricWidth')" min-width="100px">
            <template slot-scope="scope">
              <span>{{ scope.row['actualFabricWidthInCm'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['yarnNumber']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option v-for="item in yarns" :key="item['number']" :label="item['number']" :value="item['number']" />
            </el-select>
          </template>
          <el-table-column :label="$t('fabric.yarn')">
            <template slot-scope="scope">
              <span>{{ scope.row['yarnNumber'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-date-picker
              v-model="search['orderedAt']"
              type="date"
              class="search"
              :placeholder="$t('table.searchDatePlaceholder')"
              size="mini"
              :format="dateFormat"
              :value-format="dateFormat"
              clearable
              style="width: 100%"
            />
          </template>
          <el-table-column :label="$t('orderLine.orderDate')" min-width="110px" sortable="custom" prop="orderedAt">
            <template slot-scope="scope">
              <span>
                <date-time-picker v-model="scope.row['orderedAt']" :format="dateFormat" disabled />
              </span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-date-picker
              v-model="search['deliveryDate']"
              type="date"
              class="search"
              :placeholder="$t('table.searchDatePlaceholder')"
              size="mini"
              :format="dateFormat"
              :value-format="dateFormat"
              clearable
              style="width: 100%"
            />
          </template>
          <el-table-column :label="$t('orderLine.deliveryDate')" width="115px" sortable="custom" prop="deliveryDate">
            <template slot-scope="scope">
              <span>
                <date-time-picker v-model="scope.row['deliveryDate']" :format="dateFormat" disabled />
              </span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['seriesNumber']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.seriesNumber')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.seriesNumber')" width="110px">
            <template slot-scope="scope">
              <span>{{ scope.row['seriesNumber'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['workshopID']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option v-for="item in workshops" :key="item['name']" :label="item['name']" :value="item['id']" />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.workshop')" min-width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['plannedWorkshopName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['productGroupID']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in productGroups"
                :key="item['description']"
                :label="item['description']"
                :value="item['id']"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.productGroup')" min-width="120px">
            <template slot-scope="scope">
              <span>{{ scope.row['productGroupDescription'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['orderLineStatus']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in $store.getters['option/loadedOptions']('orderLineStatus')"
                :key="item['code']"
                :label="item['description']"
                :value="item['code']"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.orderLineStatus')" min-width="100px">
            <template slot-scope="scope">
              <span>{{ formatOrderLineStatus(scope.row['orderLineStatus']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['orderLineStatusID']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in orderLineStatuses"
                :key="item['id']"
                :label="item['name']"
                :value="item['id']"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.orderLineSubstatus')" min-width="100px">
            <template slot-scope="scope">
              <span>{{ scope.row['orderLineSubstatusName'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['transportEAN']"
              class="search"
              size="mini"
              :placeholder="$t('orderLine.transportEAN')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('orderLine.transportEAN')" min-width="150px">
            <template slot-scope="scope">
              <span>{{ scope.row['transportEAN'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['orderExceedance']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option
                v-for="item in $store.getters['option/loadedOptions']('orderExceedance')"
                :key="item['code']"
                :label="item['description']"
                :value="item['code']"
              />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.exceedance')" width="120px">
            <template slot-scope="scope">
              <span>{{ formatOrderExceedance(scope.row['orderExceedance']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-input
              v-model="search['source']"
              class="search"
              size="mini"
              :placeholder="$t('order.source')"
              clearable
              prefix-icon="el-icon-search"
            />
          </template>
          <el-table-column :label="$t('order.source')" min-width="100px">
            <template slot-scope="scope">
              <span>{{ scope.row['source'] }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['deliverAtHomeYN']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option :key="true" :label="$t('orderLine.deliver')" :value="true" />
              <el-option :key="false" :label="$t('orderLine.pickUp')" :value="false" />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.delivery')" width="90px">
            <template slot-scope="scope">
              <span>{{ formatDeliverAtHomeYN(scope.row['deliverAtHomeYN']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['installationServiceYN']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option :key="true" :label="$t('common.yes')" :value="true" />
              <el-option :key="false" :label="$t('common.no')" :value="false" />
            </el-select>
          </template>
          <el-table-column :label="$t('common.service')" width="100px">
            <template slot-scope="scope">
              <span>{{ formatInstallationServiceYN(scope.row['installationServiceYN']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>

        <el-table-column label-class-name="filter-cell">
          <template slot="header" slot-scope="{}">
            <el-select
              v-model="search['urgentYN']"
              class="search-select"
              size="mini"
              :placeholder="$t('table.searchSelectPlaceholder')"
              clearable
            >
              <el-option :key="true" :label="$t('common.yes')" :value="true" />
              <el-option :key="false" :label="$t('common.no')" :value="false" />
            </el-select>
          </template>
          <el-table-column :label="$t('orderLine.urgent')" width="100px">
            <template slot-scope="scope">
              <span>{{ formatUrgentYN(scope.row['urgentYN']) }}</span>
            </template>
          </el-table-column>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import CrudTablePersistentState from '@/components/crud/CrudTablePersistentState';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import EditorButton from '@/components/crud/EditorButton';
import { closeCurrentTag } from '@/utils/tags';

const debounce = require('lodash.debounce');
const persistentState = new CrudTablePersistentState();
const FirstPage = 1;

export default {
  components: {
    DateTimePicker,
    EditorButton
  },
  data() {
    const routeFilters = this.$route.params.filters;
    const search =
      routeFilters && Object.keys(routeFilters).length !== 0
        ? routeFilters // ignore other filters
        : persistentState.getDefaultSearch({}, {});
    return {
      count: null,
      counting: false,
      page: FirstPage,
      pageSize: 50,
      hasNext: false,
      items: [],
      loading: false,
      search: search,
      sort: persistentState.getDefaultListQuery(this.limit).sort
    };
  },
  computed: {
    currentPage() {
      return this.page;
    },
    dateFormat() {
      return this.$store.getters.dateFormat;
    },
    hasPrevious() {
      return this.currentPage > 1;
    },
    listParams() {
      const params = {};
      if (Object.keys(this.search).length > 0) {
        params.query = { ...this.search };
      } else {
        params.query = {};
      }
      return params;
    },
    orderLineStatuses() {
      return this.$store.getters['orderLineStatus/loadedItems'];
    },
    productGroups() {
      return this.$store.getters['productGroup/loadedItems'];
    },
    workshops() {
      return this.$store.getters['workshop/loadedItems'];
    },
    yarns() {
      return this.$store.getters['yarn/loadedItems'];
    }
  },
  watch: {
    search: {
      deep: true,
      handler: function(value) {
        this.debouncedUpdateSearch();
      }
    }
  },
  created() {
    this.$store.dispatch('productGroup/getItems');
    this.$store.dispatch('workshop/getItems');
    this.$store.dispatch('yarn/getItems');
    this.$store.dispatch('orderLineStatus/getItems');
    this.debouncedUpdateSearch = debounce(this.loadFirstPage, 1000);
    let page;
    const routeFilters = this.$route.params.filters;
    if (routeFilters && Object.keys(routeFilters).length !== 0) {
      page = FirstPage;
      this.sort = '';
    } else {
      const listQuery = persistentState.getDefaultListQuery(this.pageSize);
      page = listQuery.page || FirstPage;
    }
    this.page = page;
    this.loadPage(page);
  },
  methods: {
    clearAllFilters() {
      this.search = {};
    },
    close() {
      closeCurrentTag(this);
    },
    countItems() {
      const params = this.listParams;
      this.counting = true;
      this.$store
        .dispatch('orderLine/count', {
          filters: params.query
        })
        .then(data => {
          this.count = data;
        })
        .finally(() => {
          this.counting = false;
        });
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    },
    filtersActive() {
      return Object.keys(this.search).some(k => this.search[k]);
    },
    formatDeliverAtHomeYN(cellValue) {
      return cellValue ? this.$t('orderLine.deliver') : this.$t('orderLine.pickUp');
    },
    formatFabricStatus(cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('fabricStatus').find(item => item.code === cellValue);
      return option && option.description;
    },
    formatInstallationServiceYN(cellValue) {
      return cellValue ? this.$t('planning.installationService') : '';
    },
    formatOrderExceedance(cellValue) {
      const option = this.$store.getters['option/loadedOptions']('orderExceedance').find(
        item => item.code === cellValue
      );
      return option && option.description;
    },
    formatOrderLineStatus(cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('orderLineStatus').find(item => item.code === cellValue);
      return option && option.description;
    },
    formatUrgentYN(cellValue) {
      return cellValue ? this.$t('orderLine.urgent') : '';
    },
    loadFirstPage() {
      this.count = null;
      this.refreshItems(FirstPage);
    },
    loadPage(page) {
      if (page < 0) {
        page = 0;
      }
      this.refreshItems(page);
    },
    nextPage() {
      this.refreshItems(this.page + 1);
    },
    previousPage() {
      this.refreshItems(this.page - 1);
    },
    refreshItems(page) {
      const params = this.listParams;
      params.query['page'] = page;
      params.query['limit'] = this.pageSize;
      params.query['sort'] = this.sort;
      persistentState.persistListParams(params);
      this.loading = true;
      this.$store
        .dispatch('orderLine/getPage', {
          filters: params.query
        })
        .then(data => {
          this.page = page;
          this.items = Object.freeze(data.items);
          this.hasNext = data.hasNext;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    rowDblClick(row) {
      const { orderLineID } = row;
      this.$router.push({ name: 'EditOrderLine', params: { id: orderLineID } });
    },
    showCount() {
      return this.count !== null && this.count !== undefined;
    },
    sortChange({ column, prop, order }) {
      if (prop !== null) {
        this.sort = order && (order === 'descending' ? prop + '_desc' : prop);
      }
      this.loadFirstPage();
    }
  }
};
</script>

<style lang="scss">
.el-table--medium td,
.el-table--medium th {
  padding: 0px 0;
}

.crud-table {
  .edit-input {
    padding-right: 100px;
  }

  .cancel-btn {
    position: absolute;
    right: 15px;
    top: 10px;
  }
  .custom-actions + .el-button {
    margin-left: 10px;
  }
  .filter-cell > div {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .hide-overflow {
    .cell {
      white-space: nowrap;
    }
  }
  .search {
    .el-input__inner {
      padding-left: 25px;
      padding-right: 20px;
    }
    .el-input__prefix {
      left: 0;
    }
    .el-input__suffix {
      right: 0;
    }
  }
  .search-select {
    width: 100%;
    .el-input__inner {
      padding-left: 5px;
    }
    .el-input {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
  .el-table .cell {
    word-break: break-word;
  }
  .checkbox {
    padding-left: 12px;
  }
}

.topbar {
  margin-bottom: 5px;
}

.counter {
  padding-left: 8px;
  font-size: 14px;
  font-family: Arial, sans-serif;
}

.pager {
  .page-number {
    padding-left: 8px;
    font-size: 14px;
    font-family: Arial, sans-serif;
  }
  .prev {
    margin-left: 0px;
  }
}
</style>
